import * as stylex from '@stylexjs/stylex'
import { useCallback, useEffect, useId, useState } from 'react'

import Avatar from '../../components/Avatar'
import Button from '../../components/Button'
import Dialog from '../../components/Dialog'
import Icon from '../../components/Icon'
import LoanTermsFields from '../../components/LoanTermsFields'
import { defaultExtraTerms, parseExtraTerms } from '../../lib/loanTerms'
import type { Overview, Loan, LoanBalance, Person, CreateLoanInput } from '../../lib/ledger'
import { centsToInput, currencyOptions, findPerson, formatMoney, parseCents, v1API } from '../../lib/ledger'
import { calendarDay, estimateLoan } from '../../lib/loanEstimate'
import { monthlyInstallments } from '../../lib/installments'

export type LoanWithBalance = { loan: Loan; balance: LoanBalance }

const pulse = stylex.keyframes({
  from: { opacity: 1 },
  '50%': { opacity: 0.55 },
  to: { opacity: 1 },
})

const styles = stylex.create({
  s0: {
    fontSize: "11px",
    fontWeight: 500,
    opacity: 0.75,
  },
  s1: {
    marginTop: "8px",
    fontSize: "21px",
    fontWeight: 600,
    letterSpacing: "-.03em",
  },
  s2: {
    display: "flex",
    flexDirection: "column",
    rowGap: "20px",
    minWidth: 0,
    padding: 0,
    margin: 0,
  },
  s3: {
    display: "block",
  },
  s4: {
    fontSize: "12px",
    fontWeight: 600,
    color: "#46544a",
  },
  s5: {
    marginTop: "8px",
    height: "44px",
    width: "100%",
    borderRadius: "0.75rem",
    borderWidth: 1,
    borderColor: "#dbe5da",
    backgroundColor: "#fff",
    paddingInline: "12px",
    fontSize: "16px",
    minWidth: 0,
    outline: "none",
    ":focus": {
      borderColor: "#75a97c",
    },
  },
  s6: {
    marginTop: "8px",
    height: "44px",
    width: "100%",
    borderRadius: "0.75rem",
    borderWidth: 1,
    borderColor: "#dbe5da",
    paddingInline: "12px",
    fontSize: "16px",
    outline: "none",
    ":focus": {
      borderColor: "#75a97c",
    },
  },
  s7: {
    display: "grid",
    gap: "12px",
    "@media (min-width: 640px)": {
      gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    },
  },
  s8: {
    marginTop: "8px",
    display: "flex",
    height: "44px",
    overflow: "hidden",
    borderRadius: "0.75rem",
    borderWidth: 1,
    borderColor: "#dbe5da",
    ":focus-within": {
      borderColor: "#75a97c",
    },
  },
  s9: {
    minWidth: "0px",
    flex: "1 1 0%",
    paddingInline: "12px",
    fontSize: "16px",
    outline: "none",
  },
  s10: {
    borderLeftWidth: 1,
    borderColor: "#dbe5da",
    backgroundColor: "#f9fbf8",
    paddingInline: "8px",
    fontSize: "16px",
    color: "#4f6255",
    outline: "none",
  },
  s11: {
    display: "flex",
    alignItems: "center",
    borderLeftWidth: 1,
    borderColor: "#dbe5da",
    backgroundColor: "#f9fbf8",
    paddingInline: "12px",
    fontSize: "12px",
    color: "#718077",
  },
  s12: {
    fontWeight: 400,
    color: "#8a958c",
  },
  checkbox: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    minHeight: "44px",
    fontSize: "14px",
    color: "#46544a",
  },
  s13: {
    marginTop: "8px",
    height: "44px",
    width: "100%",
    borderRadius: "0.75rem",
    borderWidth: 1,
    borderColor: "#dbe5da",
    paddingInline: "12px",
    fontSize: "16px",
    minWidth: 0,
    outline: "none",
    ":focus": {
      borderColor: "#75a97c",
    },
  },
  s14: {
    borderRadius: "1rem",
    borderWidth: 1,
    borderColor: "#d9e8d8",
    backgroundColor: "#f5fbf3",
    padding: "16px",
  },
  s15: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  s16: {
    fontSize: "12px",
    fontWeight: 600,
    color: "#3d6245",
  },
  s17: {
    marginTop: "4px",
    fontSize: "11px",
    color: "#718075",
  },
  s18: {
    height: "20px",
    width: "20px",
    color: "#4d9360",
  },
  s19: {
    marginTop: "16px",
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: "12px",
    "@media (min-width: 640px)": {
      gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    },
  },
  s20: {
    fontSize: "10px",
    color: "#7a8b7d",
  },
  s21: {
    marginTop: "4px",
    fontSize: "14px",
    fontWeight: 600,
    color: "#2f7041",
    overflowWrap: "anywhere",
  },
  s22: {
    borderRadius: "0.75rem",
    backgroundColor: "#fff1eb",
    paddingInline: "12px",
    paddingBlock: "10px",
    fontSize: "12px",
    lineHeight: "1.25rem",
    color: "#a44e20",
  },
  s23: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) minmax(0, 2fr)",
    gap: "8px",
    "@media (min-width: 640px)": { gridTemplateColumns: "auto auto", justifyContent: "end" },
  },
  s24: {
    borderRadius: "0.75rem",
    backgroundColor: "#f4f9f2",
    padding: "16px",
  },
  s25: {
    fontSize: "11px",
    fontWeight: 500,
    color: "#66806c",
  },
  s26: {
    marginTop: "4px",
    fontSize: "23px",
    fontWeight: 600,
    color: "#2d7342",
  },
  s27: {
    marginTop: "8px",
    height: "48px",
    width: "100%",
    borderRadius: "0.75rem",
    borderWidth: 1,
    borderColor: "#dbe5da",
    paddingInline: "12px",
    fontSize: "16px",
    outline: "none",
    ":focus": {
      borderColor: "#75a97c",
    },
  },
  s28: {
    borderRadius: "1rem",
    borderWidth: 1,
    borderColor: "#e1e8e1",
    backgroundColor: "#fff",
    padding: "16px",
    boxShadow: "0 4px 16px rgba(48,70,51,.035)",
    "@media (min-width: 640px)": {
      padding: "20px",
    },
  },
  s29: {
    display: "flex",
    alignItems: "start",
    justifyContent: "space-between",
    gap: "16px",
  },
  s30: {
    display: "flex",
    minWidth: "0px",
    alignItems: "center",
    gap: "12px",
  },
  s31: {
    minWidth: "0px",
  },
  s32: {
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    fontSize: "14px",
    fontWeight: 600,
    color: "#283229",
  },
  s33: {
    marginTop: "2px",
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    fontSize: "11px",
    color: "#768178",
  },
  s34: {
    marginTop: "20px",
    display: "flex",
    alignItems: "end",
    justifyContent: "space-between",
    gap: "16px",
  },
  s35: {
    fontSize: "11px",
    color: "#778379",
  },
  s36: {
    marginTop: "4px",
    fontSize: "25px",
    fontWeight: 600,
    letterSpacing: "-.035em",
    color: "#27342b",
  },
  s37: {
    textAlign: "right",
    fontSize: "11px",
    lineHeight: "1.25rem",
    color: "#78847b",
  },
  s38: {
    marginTop: "16px",
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: "8px",
    borderTopWidth: 1,
    borderColor: "#edf0eb",
    paddingTop: "12px",
  },
  s39: {
    fontSize: "10px",
    color: "#849087",
  },
  s40: {
    marginTop: "4px",
    fontSize: "12px",
    fontWeight: 600,
    color: "#425148",
  },
  s41: {
    marginTop: "16px",
    display: "flex",
    height: "40px",
    width: "100%",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    borderRadius: "0.75rem",
    backgroundColor: "#eef6eb",
    fontSize: "12px",
    fontWeight: 600,
    color: "#397b49",
    transition: "color, background-color, border-color, text-decoration-color, fill, stroke, opacity, box-shadow, transform, filter, backdrop-filter",
    ":hover": {
      backgroundColor: "#e2f0df",
    },
  },
  s42: {
    height: "16px",
    width: "16px",
  },
  s43: {
    display: "flex",
    flexDirection: "column",
    rowGap: "24px",
    "@media (min-width: 640px)": {
      display: "flex",
      flexDirection: "column",
      rowGap: "28px",
    },
  },
  s44: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    gap: "20px",
    "@media (min-width: 640px)": {
      flexDirection: "row",
      alignItems: "end",
    },
  },
  s45: {
    fontSize: "13px",
    fontWeight: 500,
    color: "#5b9164",
  },
  s46: {
    marginTop: "4px",
    fontSize: "clamp(1.75rem,3.2vw,2.25rem)",
    fontWeight: 600,
    letterSpacing: "-.045em",
    color: "#202720",
  },
  s47: {
    marginTop: "8px",
    maxWidth: "36rem",
    fontSize: "14px",
    color: "#707b72",
  },
  s48: {
    display: "flex",
    height: "40px",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    borderRadius: "0.75rem",
    backgroundColor: "#3f8955",
    paddingInline: "16px",
    fontSize: "13px",
    fontWeight: 600,
    color: "#fff",
    boxShadow: "0 1px 2px 0 rgb(0 0 0 / 0.05)",
    transition: "color, background-color, border-color, text-decoration-color, fill, stroke, opacity, box-shadow, transform, filter, backdrop-filter",
    ":hover": {
      backgroundColor: "#347c49",
    },
  },
  s49: {
    display: "grid",
    gap: "12px",
    "@media (min-width: 768px)": {
      gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    },
  },
  s50: {
    display: "grid",
    gap: "16px",
    "@media (min-width: 1024px)": {
      gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    },
  },
  s51: {
    height: "240px",
    borderRadius: "1rem",
    backgroundColor: "#eaf0e8",
    animationName: pulse,
    animationDuration: "1.6s",
    animationTimingFunction: "ease-in-out",
    animationIterationCount: "infinite",
  },
  s52: {
    borderRadius: "1rem",
    borderWidth: 1,
    borderColor: "#eadbd2",
    backgroundColor: "#fff",
    padding: "28px",
    textAlign: "center",
  },
  s53: {
    fontSize: "14px",
    fontWeight: 600,
    color: "#6d4131",
  },
  s54: {
    marginTop: "8px",
    fontSize: "12px",
    color: "#7e756f",
  },
  s55: {
    marginTop: "16px",
    height: "36px",
    borderRadius: "0.75rem",
    backgroundColor: "#eef6eb",
    paddingInline: "16px",
    fontSize: "12px",
    fontWeight: 600,
    color: "#397849",
  },
  s56: {
    borderRadius: "1rem",
    borderWidth: 1,
    borderStyle: "dashed",
    borderColor: "#cbdcca",
    backgroundColor: "#fbfdfb",
    paddingInline: "24px",
    paddingBlock: "56px",
    textAlign: "center",
  },
  s57: {
    marginInline: "auto",
    display: "flex",
    height: "48px",
    width: "48px",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "1rem",
    backgroundColor: "#eef6eb",
    color: "#438254",
  },
  s58: {
    height: "24px",
    width: "24px",
  },
  s59: {
    marginTop: "16px",
    fontSize: "16px",
    fontWeight: 600,
    color: "#2c382e",
  },
  s60: {
    marginInline: "auto",
    marginTop: "8px",
    maxWidth: "24rem",
    fontSize: "13px",
    lineHeight: "1.25rem",
    color: "#78847b",
  },
  s61: {
    marginTop: "20px",
    height: "40px",
    borderRadius: "0.75rem",
    backgroundColor: "#3f8955",
    paddingInline: "16px",
    fontSize: "12px",
    fontWeight: 600,
    color: "#fff",
  },
  s62: {
    position: "fixed",
    bottom: "20px",
    right: "20px",
    zIndex: 120,
    maxWidth: "24rem",
    borderRadius: "0.75rem",
    backgroundColor: "#243c2b",
    paddingInline: "16px",
    paddingBlock: "12px",
    fontSize: "12px",
    fontWeight: 500,
    color: "#fff",
    boxShadow: "0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)",
  },
  s63: {
    marginLeft: "12px",
    color: "color-mix(in srgb, #fff 70%, transparent)",
    ":hover": {
      color: "#fff",
    },
  },
})

const loanVariantStyles = stylex.create({
  status: { borderRadius: '9999px', paddingInline: '10px', paddingBlock: '4px', fontSize: '10px', fontWeight: 600 },
  paid: { backgroundColor: '#edf7ef', color: '#347a47' },
  overdue: { backgroundColor: '#fff1eb', color: '#a44e20' },
  cancelled: { backgroundColor: '#f1f2ef', color: '#68746b' },
  open: { backgroundColor: '#eef4ff', color: '#426b9d' },
  metric: { borderRadius: '1rem', borderWidth: 1, paddingInline: '16px', paddingBlock: '16px' },
  metricGreen: { borderColor: '#d7e9d7', backgroundColor: '#f5fbf3', color: '#2e7744' },
  metricOrange: { borderColor: '#f1ded0', backgroundColor: '#fff9f3', color: '#a45525' },
  metricNeutral: { borderColor: '#e1e8e1', backgroundColor: '#fff', color: '#27342b' },
})
type LoansPageProps = { data: Overview; previewLoans?: LoanWithBalance[] }

function rateLabel(bps: number) {
  return `${(bps / 100).toFixed(2)}% / month`
}

function personFor(data: Overview, id: string) {
  return findPerson(data, id) as Person | undefined
}

function statusLabel(status: Loan['status']) {
  return status === 'OVERDUE' ? 'Overdue' : status.charAt(0) + status.slice(1).toLowerCase()
}

function statusStyle(status: Loan['status']) {
  if (status === 'PAID') return loanVariantStyles.paid
  if (status === 'OVERDUE') return loanVariantStyles.overdue
  if (status === 'CANCELLED') return loanVariantStyles.cancelled
  return loanVariantStyles.open
}

function Metric({ label, value, tone }: { label: string; value: string; tone: 'green' | 'orange' | 'neutral' }) {
  const toneStyle = tone === 'green' ? loanVariantStyles.metricGreen : tone === 'orange' ? loanVariantStyles.metricOrange : loanVariantStyles.metricNeutral
  return <div {...stylex.props(loanVariantStyles.metric, toneStyle)}><p {...stylex.props(styles.s0)}>{label}</p><p {...stylex.props(styles.s1)}>{value}</p></div>
}

export function LoanForm({ data, onClose, onSave, preset, initialLoan }: { data: Overview; onClose: () => void; onSave: (input: CreateLoanInput) => Promise<void>; preset?: { personID: string; principalCents?: number; currency?: string }; initialLoan?: Loan }) {
  const formID = useId()
  const [borrowerID, setBorrowerID] = useState(preset?.personID ?? (initialLoan ? initialLoan.lenderPersonId === data.currentUser.id ? initialLoan.borrowerPersonId : initialLoan.lenderPersonId : data.people.find((person) => person.id !== data.currentUser.id)?.id ?? ''))
  const [direction, setDirection] = useState<'lent' | 'borrowed'>(initialLoan && initialLoan.borrowerPersonId === data.currentUser.id ? 'borrowed' : 'lent')
  const [principal, setPrincipal] = useState(preset?.principalCents ? centsToInput(preset.principalCents) : initialLoan ? centsToInput(initialLoan.principalCents) : '')
  const [currency, setCurrency] = useState(preset?.currency ?? initialLoan?.currency ?? data.currentUser.currency)
  const [issuedAt, setIssuedAt] = useState(() => {
    const date = new Date(initialLoan?.issuedAt ?? Date.now())
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
  })
  const [feeType, setFeeType] = useState<'fixed' | 'percentage'>('fixed')
  const [fee, setFee] = useState('0')
  const [interest, setInterest] = useState(centsToInput(initialLoan?.monthlyInterestBps ?? 0))
  const [compound, setCompound] = useState(initialLoan?.compound ?? false)
  const [dueAt, setDueAt] = useState(initialLoan?.dueAt?.slice(0, 10) ?? '')
  const [installmentCount, setInstallmentCount] = useState('1')
  const [firstInstallmentDue, setFirstInstallmentDue] = useState('')
  const [extra, setExtra] = useState({ ...defaultExtraTerms, interestType: initialLoan?.monthlyInterestCents ? 'fixed' as const : 'percentage' as const, fixedInterest: centsToInput(initialLoan?.monthlyInterestCents ?? 0), interestStarts: initialLoan?.interestStartsAt?.slice(0, 10) ?? '', penaltyType: initialLoan?.penalty?.type ?? 'none', penaltyAmount: centsToInput(initialLoan?.penalty?.type === 'percentage' ? initialLoan.penalty.rateBps ?? 0 : initialLoan?.penalty?.amountCents ?? 0), frequency: initialLoan?.penalty?.frequency ?? 'monthly', grace: String(initialLoan?.penalty?.graceDays ?? 0), cap: initialLoan?.penalty?.capCents ? centsToInput(initialLoan.penalty.capCents) : '' })
  const extraParsed = parseExtraTerms(extra, issuedAt, dueAt)
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)
  const principalCents = parseCents(principal) ?? 0
  const installments = Number(installmentCount) > 1 ? monthlyInstallments(principalCents, Number(installmentCount), firstInstallmentDue) : []
  const feeValue = parseCents(fee)
  const interestBps = extra.interestType === 'percentage' ? parseCents(interest) : 0
  const feeCents = feeType === 'fixed' ? feeValue ?? -1
    : feeValue !== null && feeValue <= 10000 ? Number(BigInt(principalCents) * BigInt(feeValue) / 10000n) : -1
  const estimate = estimateLoan({ principalCents, feeCents, monthlyInterestBps: interestBps ?? -1, monthlyInterestCents: extraParsed.monthlyInterestCents, interestStartsAt: extra.interestStarts || undefined, compound: extra.interestType === 'percentage' && compound, issuedAt, dueAt })
  const validationError = (Number(installmentCount) > 1 && (!installments || firstInstallmentDue < issuedAt) ? 'Choose 2–120 installments and a first due date on or after issue.' : '') || extraParsed.error || (!calendarDay(issuedAt) ? 'Choose a valid loan date.'
    : dueAt && (!calendarDay(dueAt) || dueAt < issuedAt) ? 'Choose a due date on or after the loan date.'
    : feeValue === null || feeCents < 0 ? 'Enter a valid fee. Percentage fees cannot exceed 100%.'
    : interestBps === null || interestBps > 10000 ? 'Monthly interest must be between 0% and 100%, with up to two decimal places.'
    : principal && principalCents <= 0 ? 'Enter a positive principal with up to two decimal places.'
    : principalCents > 0 && !estimate ? 'The date range or estimated total is too large. Check the loan terms.' : '')
  const valid = Boolean(borrowerID && borrowerID !== data.currentUser.id && estimate && !validationError)

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    if (saving) return
    setError('')
    if (!valid || feeValue === null || interestBps === null) {
      setError(validationError || 'Choose the other person and enter a principal amount.')
      return
    }
    setSaving(true)
    try {
      await onSave({
        lenderPersonId: direction === 'lent' ? data.currentUser.id : borrowerID,
        borrowerPersonId: direction === 'lent' ? borrowerID : data.currentUser.id,
        principalCents,
        installments: initialLoan?.installments ?? installments ?? undefined,
        currency,
        issuedAt: `${issuedAt}T12:00:00.000Z`,
        feeType,
        feeAmountCents: feeType === 'fixed' ? feeValue : undefined,
        feeRateBps: feeType === 'percentage' ? feeValue : undefined,
        monthlyInterestBps: interestBps,
        monthlyInterestCents: extraParsed.monthlyInterestCents,
        interestStartsAt: extraParsed.interestStartsAt,
        penalty: extraParsed.penalty,
        accrual: 'daily',
        compound: extra.interestType === 'percentage' && compound,
        dueAt: dueAt ? `${dueAt}T23:59:59.000Z` : undefined,
      })
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Could not create the loan.')
    } finally {
      setSaving(false)
    }
  }

  return <Dialog description={initialLoan ? 'Updated terms apply prospectively. Existing charges are preserved.' : 'Set the amount, dates, and repayment terms.'} closeDisabled={saving} onClose={onClose} title={initialLoan ? 'Edit loan terms' : preset?.principalCents ? 'Convert expenses to a loan' : 'Create a loan'} footer={
    <div {...stylex.props(styles.s23)}><Button disabled={saving} onClick={onClose} variant="secondary" type="button">Cancel</Button><Button disabled={!valid || saving} form={formID} type="submit">{saving ? 'Saving…' : initialLoan ? 'Save terms' : preset?.principalCents ? 'Confirm conversion' : 'Create loan'}</Button></div>
  }><form id={formID} onSubmit={submit}><fieldset {...stylex.props(styles.s2)} disabled={saving}>
    <fieldset {...stylex.props(styles.s2)} disabled={Boolean(preset?.principalCents || initialLoan)}>
    <div {...stylex.props(styles.s7)}><label {...stylex.props(styles.s3)}><span {...stylex.props(styles.s4)}>Money flow</span><select {...stylex.props(styles.s5)} onChange={(event) => setDirection(event.target.value as 'lent' | 'borrowed')} value={direction}><option value="lent">You lend money</option><option value="borrowed">You borrow money</option></select></label><label {...stylex.props(styles.s3)}><span {...stylex.props(styles.s4)}>Currency</span><select {...stylex.props(styles.s5)} onChange={(event) => setCurrency(event.target.value)} value={currency}>{currencyOptions(data.currentUser.currency).map((code) => <option key={code} value={code}>{code}</option>)}</select></label></div>
    <label {...stylex.props(styles.s3)}><span {...stylex.props(styles.s4)}>{direction === 'lent' ? 'Borrower' : 'Lender'}</span><select {...stylex.props(styles.s5)} onChange={(event) => setBorrowerID(event.target.value)} value={borrowerID}>{data.people.filter((person) => person.id !== data.currentUser.id).map((person) => <option key={person.id} value={person.id}>{person.name}</option>)}</select></label>
    {!borrowerID && <p {...stylex.props(styles.s22)}>Add a person from People before creating a loan.</p>}
    <label {...stylex.props(styles.s3)}><span {...stylex.props(styles.s4)}>Principal ({currency})</span><input {...stylex.props(styles.s6)} data-dialog-autofocus inputMode="decimal" onChange={(event) => setPrincipal(event.target.value)} placeholder="0.00" value={principal} /></label>
    </fieldset>
    <div {...stylex.props(styles.s7)}>
      {!preset?.principalCents && !initialLoan && <label {...stylex.props(styles.s3)}><span {...stylex.props(styles.s4)}>Fee</span><div {...stylex.props(styles.s8)}><input {...stylex.props(styles.s9)} inputMode="decimal" onChange={(event) => setFee(event.target.value)} value={fee} /><select aria-label="Fee type" {...stylex.props(styles.s10)} onChange={(event) => setFeeType(event.target.value as 'fixed' | 'percentage')} value={feeType}><option value="fixed">{currency}</option><option value="percentage">%</option></select></div></label>}
      {extra.interestType === 'percentage' && <label {...stylex.props(styles.s3)}><span {...stylex.props(styles.s4)}>Monthly interest (%)</span><div {...stylex.props(styles.s8)}><input {...stylex.props(styles.s9)} inputMode="decimal" onChange={(event) => setInterest(event.target.value)} value={interest} /><span {...stylex.props(styles.s11)}>%</span></div></label>}
    </div>
    <div {...stylex.props(styles.s7)}>
      <label {...stylex.props(styles.s3)}><span {...stylex.props(styles.s4)}>Loan date</span><input {...stylex.props(styles.s13)} disabled={Boolean(preset?.principalCents || initialLoan)} onChange={(event) => setIssuedAt(event.target.value)} required type="date" value={issuedAt} /></label>
      <label {...stylex.props(styles.s3)}><span {...stylex.props(styles.s4)}>Due date <span {...stylex.props(styles.s12)}>(optional)</span></span><input {...stylex.props(styles.s13)} disabled={Number(installmentCount)>1 || Boolean(initialLoan?.installments?.length)} min={issuedAt} onChange={(event) => setDueAt(event.target.value)} type="date" value={dueAt} /></label>
    </div>
    <LoanTermsFields value={extra} onChange={setExtra} currency={currency} issuedAt={issuedAt} dueAt={dueAt} firstInstallmentDue={Number(installmentCount)>1 ? firstInstallmentDue : initialLoan?.installments?.[0]?.dueAt.slice(0,10)} />
    {!initialLoan && <section {...stylex.props(styles.s2)} aria-label="Installment plan">
      <label {...stylex.props(styles.s3)}><span {...stylex.props(styles.s4)}>Principal repayment plan</span><select {...stylex.props(styles.s5)} value={installmentCount} onChange={(e) => { setInstallmentCount(e.target.value); setFirstInstallmentDue('') }}><option value="1">Single due date</option>{[2,3,4,6,12,24].map((count) => <option key={count} value={count}>{count} monthly installments</option>)}</select></label>
      {Number(installmentCount) > 1 && <label {...stylex.props(styles.s3)}><span {...stylex.props(styles.s4)}>First installment due</span><input {...stylex.props(styles.s13)} type="date" min={issuedAt} value={firstInstallmentDue} onChange={(e) => { setFirstInstallmentDue(e.target.value); const plan = monthlyInstallments(principalCents, Number(installmentCount), e.target.value); if (plan) setDueAt(plan[plan.length - 1].dueAt.slice(0,10)) }} /></label>}
      {installments && installments.length > 0 && <><p {...stylex.props(styles.s17)}>Principal is allocated oldest-installment-first. Interest and fees are separate. Penalties apply only to overdue installment principal after its grace period; fixed penalties are per overdue installment.</p>{installments.map((item) => <p {...stylex.props(styles.s17)} key={item.dueAt}>{item.dueAt.slice(0,10)} · {formatMoney(item.principalCents,currency)}</p>)}</>}
    </section>}
    <label {...stylex.props(styles.checkbox)}><input checked={extra.interestType === 'percentage' && compound} disabled={extra.interestType === 'fixed'} onChange={(event) => setCompound(event.target.checked)} type="checkbox" /><span>Compound interest</span></label>
    {compound && extra.interestType === 'percentage' && <p {...stylex.props(styles.s17)}>Unpaid interest earns interest each day.</p>}
    {estimate && !validationError && !initialLoan && <section {...stylex.props(styles.s14)} aria-label="Loan cost estimate">
      <div {...stylex.props(styles.s15)}><div><p {...stylex.props(styles.s16)}>Estimated cost</p><p {...stylex.props(styles.s17)}>{dueAt ? `Through ${estimate.through}` : 'Over 365 days from the loan date'} · assumes no repayments</p></div><Icon {...stylex.props(styles.s18)} name="calendar" /></div>
      <div {...stylex.props(styles.s19)}>
        <div><p {...stylex.props(styles.s20)}>Principal</p><p {...stylex.props(styles.s21)}>{formatMoney(principalCents, currency)}</p></div>
        <div><p {...stylex.props(styles.s20)}>{compound ? 'Compound interest' : 'Interest'}</p><p {...stylex.props(styles.s21)}>{formatMoney(estimate.interestCents, currency)}</p></div>
        <div><p {...stylex.props(styles.s20)}>Fees</p><p {...stylex.props(styles.s21)}>{formatMoney(feeCents, currency)}</p></div>
        <div><p {...stylex.props(styles.s20)}>Total due</p><p {...stylex.props(styles.s21)}>{formatMoney(estimate.totalCents, currency)}</p></div>
      </div>
    </section>}
    {(error || validationError) && <p {...stylex.props(styles.s22)} role="alert">{error || validationError}</p>}
  </fieldset></form></Dialog>
}

export function PaymentForm({ item, onClose, onSave }: { item: LoanWithBalance; onClose: () => void; onSave: (loanID: string, amountCents: number) => Promise<void> }) {
  const formID = useId()
  const [amount, setAmount] = useState('')
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)
  const amountCents = parseCents(amount)
  const valid = amountCents !== null && amountCents > 0 && amountCents <= item.balance.outstandingCents
   const penaltyPaid = Math.min(amountCents ?? 0, item.balance.penaltyOutstandingCents ?? 0)
   const interestPaid = Math.min((amountCents ?? 0) - penaltyPaid, item.balance.interestOutstandingCents)
  const feePaid = Math.min((amountCents ?? 0) - penaltyPaid - interestPaid, item.balance.feeOutstandingCents)
  const principalPaid = (amountCents ?? 0) - penaltyPaid - interestPaid - feePaid
  async function submit(event: React.FormEvent) {
    event.preventDefault()
    if (saving) return
    if (!valid || amountCents === null) {
      setError(`Enter an amount up to ${formatMoney(item.balance.outstandingCents, item.loan.currency)}.`)
      return
    }
    setError('')
    setSaving(true)
    try { await onSave(item.loan.id, amountCents) } catch (reason) { setError(reason instanceof Error ? reason.message : 'Could not record that payment.') } finally { setSaving(false) }
  }
  return <Dialog description="Payments cover penalties, then interest, fees, and principal." closeDisabled={saving} onClose={onClose} title="Record a repayment" footer={
    <div {...stylex.props(styles.s23)}><Button disabled={saving} onClick={onClose} variant="secondary" type="button">Cancel</Button><Button disabled={!valid || saving} form={formID} type="submit">{saving ? 'Saving…' : 'Record payment'}</Button></div>
  }>
    <form id={formID} onSubmit={submit}><fieldset {...stylex.props(styles.s2)} disabled={saving}>
      <div {...stylex.props(styles.s24)}><p {...stylex.props(styles.s25)}>Outstanding balance</p><p {...stylex.props(styles.s26)}>{formatMoney(item.balance.outstandingCents, item.loan.currency)}</p></div>
      <label {...stylex.props(styles.s3)}><span {...stylex.props(styles.s4)}>Payment amount ({item.loan.currency})</span><input {...stylex.props(styles.s27)} data-dialog-autofocus inputMode="decimal" onChange={(event) => setAmount(event.target.value)} placeholder="0.00" value={amount} /></label>
      <Button variant="secondary" onClick={() => setAmount(centsToInput(item.balance.outstandingCents))}>Use full balance</Button>
      {valid && <section {...stylex.props(styles.s14)} aria-label="Payment allocation">
        <p {...stylex.props(styles.s16)}>How this payment is applied</p>
        <div {...stylex.props(styles.s19)}>{[
          ['Penalties', penaltyPaid], ['Interest', interestPaid], ['Fees', feePaid], ['Principal', principalPaid], ['Remaining', item.balance.outstandingCents - (amountCents ?? 0)],
        ].map(([label, cents]) => <div key={label}><p {...stylex.props(styles.s20)}>{label}</p><p {...stylex.props(styles.s21)}>{formatMoney(Number(cents), item.loan.currency)}</p></div>)}</div>
      </section>}
      {(error || (amount && !valid)) && <p {...stylex.props(styles.s22)} role="alert">{error || `Enter an amount up to ${formatMoney(item.balance.outstandingCents, item.loan.currency)}.`}</p>}
    </fieldset></form>
  </Dialog>
}

function LoanCard({ data, item, onPay }: { data: Overview; item: LoanWithBalance; onPay: () => void }) {
  const borrower = personFor(data, item.loan.borrowerPersonId)
  const lender = personFor(data, item.loan.lenderPersonId)
  const otherPersonID = item.loan.lenderPersonId === data.currentUser.id ? item.loan.borrowerPersonId : item.loan.lenderPersonId
  return <article {...stylex.props(styles.s28)}>
    <div {...stylex.props(styles.s29)}><div {...stylex.props(styles.s30)}><Avatar person={borrower ?? { id: item.loan.borrowerPersonId, name: 'Borrower', color: 1 }} size={42} /><div {...stylex.props(styles.s31)}><p {...stylex.props(styles.s32)}>{borrower?.name ?? 'Borrower'}</p><p {...stylex.props(styles.s33)}>Lent by {lender?.name ?? 'you'}</p></div></div><span {...stylex.props(loanVariantStyles.status, statusStyle(item.loan.status))}>{statusLabel(item.loan.status)}</span></div>
    <div {...stylex.props(styles.s34)}><div><p {...stylex.props(styles.s35)}>Outstanding</p><p {...stylex.props(styles.s36)}>{formatMoney(item.balance.outstandingCents, item.loan.currency)}</p></div><p {...stylex.props(styles.s37)}>{item.loan.monthlyInterestCents ? `${formatMoney(item.loan.monthlyInterestCents, item.loan.currency)} / month` : rateLabel(item.loan.monthlyInterestBps)}<br />{item.loan.dueAt ? `Due ${item.loan.dueAt.slice(0, 10)}` : 'No due date'}</p></div>
    <div {...stylex.props(styles.s38)}>{[['Principal', item.balance.principalOutstandingCents], ['Fees', item.balance.feeOutstandingCents], ['Interest', item.balance.interestOutstandingCents], ['Penalties', item.balance.penaltyOutstandingCents ?? 0]].map(([label, amount]) => <div key={label}><p {...stylex.props(styles.s39)}>{label}</p><p {...stylex.props(styles.s40)}>{formatMoney(Number(amount), item.loan.currency)}</p></div>)}</div>
    {item.balance.outstandingCents > 0 && item.loan.status !== 'CANCELLED' && <Button onClick={onPay} variant="secondary" fullWidth>Record repayment</Button>}
    <a {...stylex.props(styles.s33)} href={`/people/${encodeURIComponent(otherPersonID)}`}>View expenses, loan terms and sharing →</a>
  </article>
}

export default function LoansPage({ data, previewLoans }: LoansPageProps) {
  const [items, setItems] = useState<LoanWithBalance[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [showCreate, setShowCreate] = useState(false)
  const [paying, setPaying] = useState<LoanWithBalance | null>(null)
  const [toast, setToast] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const loans = (await v1API.listLoans()) ?? []
      const balances = await Promise.all(loans.map(async (loan) => ({ loan, balance: await v1API.getLoanBalance(loan.id) })))
      setItems(balances)
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Could not load loans.')
    } finally { setLoading(false) }
  }, [])

  useEffect(() => {
    if (previewLoans) {
      const timeout = window.setTimeout(() => {
        setItems(previewLoans)
        setLoading(false)
      }, 0)
      return () => window.clearTimeout(timeout)
    }
    const timeout = window.setTimeout(() => { void load() }, 0)
    return () => window.clearTimeout(timeout)
  }, [load, previewLoans])

  async function createLoan(input: CreateLoanInput) {
    await v1API.createLoan(input)
    setShowCreate(false)
    setToast('Loan created. Daily interest will be tracked automatically.')
    await load()
  }

  async function createPayment(loanID: string, amountCents: number) {
    await v1API.createLoanPayment(loanID, { amountCents, method: 'manual' })
    setPaying(null)
    setToast('Repayment recorded and allocated to the loan balance.')
    await load()
  }

  const active = items.filter((item) => item.loan.status === 'ACTIVE' || item.loan.status === 'OVERDUE')
  const outstanding = active.reduce((sum, item) => sum + item.balance.outstandingCents, 0)
  const interest = active.reduce((sum, item) => sum + item.balance.interestOutstandingCents, 0)
  const currencies = new Set(active.map((item) => item.loan.currency))
  const displayCurrency = currencies.size === 1 ? [...currencies][0] : data.currentUser.currency

  return <div {...stylex.props(styles.s43)}><header {...stylex.props(styles.s44)}><div><p {...stylex.props(styles.s45)}>Borrowing, made clear</p><h1 {...stylex.props(styles.s46)}>Loans</h1><p {...stylex.props(styles.s47)}>Track principal, fees, and daily interest without awkward calculations.</p></div><button {...stylex.props(styles.s48)} onClick={() => setShowCreate(true)} type="button"><Icon {...stylex.props(styles.s42)} name="plus" />New loan</button></header>
    <section {...stylex.props(styles.s49)}><Metric label="Open loans" value={`${active.length}`} tone="neutral" /><Metric label="Outstanding" value={currencies.size === 1 ? formatMoney(outstanding, displayCurrency) : `${currencies.size} currencies`} tone="green" /><Metric label="Accrued interest" value={currencies.size === 1 ? formatMoney(interest, displayCurrency) : `${currencies.size} currencies`} tone="orange" /></section>
    {loading ? <div {...stylex.props(styles.s50)}><div {...stylex.props(styles.s51)} /><div {...stylex.props(styles.s51)} /></div> : error ? <section {...stylex.props(styles.s52)}><p {...stylex.props(styles.s53)}>We could not load your loans</p><p {...stylex.props(styles.s54)}>{error}</p><button {...stylex.props(styles.s55)} onClick={() => void load()} type="button">Try again</button></section> : active.length === 0 ? <section {...stylex.props(styles.s56)}><span {...stylex.props(styles.s57)}><Icon {...stylex.props(styles.s58)} name="loans" /></span><h2 {...stylex.props(styles.s59)}>No open loans yet</h2><p {...stylex.props(styles.s60)}>Create a loan when money changes hands. SettleUp will keep every repayment and interest accrual visible.</p><button {...stylex.props(styles.s61)} onClick={() => setShowCreate(true)} type="button">Create your first loan</button></section> : <section {...stylex.props(styles.s50)}>{active.map((item) => <LoanCard data={data} item={item} key={item.loan.id} onPay={() => setPaying(item)} />)}</section>}
    {showCreate && <LoanForm data={data} onClose={() => setShowCreate(false)} onSave={createLoan} />}{paying && <PaymentForm item={paying} onClose={() => setPaying(null)} onSave={createPayment} />}{toast && <div aria-live="polite" {...stylex.props(styles.s62)}>{toast}<button aria-label="Dismiss" {...stylex.props(styles.s63)} onClick={() => setToast('')} type="button">×</button></div>}
  </div>
}
